import { Injectable } from '@angular/core';
import { Observable, Subject, Subscription, timer } from 'rxjs';
import { take } from 'rxjs';
import { Numbers } from './shared/utils/numbers';

@Injectable({
  providedIn: 'root',
})
export class TimeoutService {
  private idleTimerSubject = new Subject<void>();
  private timeoutTimerSubject = new Subject<void>();
  private idleSubscription = new Subscription();
  private timeoutSubscription = new Subscription();
  private idleMilliseconds: number = 0;
  private timeoutMilliseconds: number = 0;
  private isTimeoutTimerStarted = false;

  /**
   * This sets the maximum length of time a user can interact with the application. When this time
   * is exceeded, the watchTimeout observable fires, which can be used to log the user out.
   * @param minutes The timeout time in minutes.
   */
  public setMaxTimeoutTime(minutes: number): void {
    this.timeoutMilliseconds = Numbers.minutesToMs(minutes);
  }

  /**
   * This sets the maximum length of time a user can be idle. When this time
   * is exceeded, the watchIdle observable fires, which can be used to log the user out.
   * @param minutes The idle time in minutes.
   */
  public setMaxIdleTime(minutes: number): void {
    this.idleMilliseconds = Numbers.minutesToMs(minutes);
  }

  /**
   * Returns an Observable that emits when the idle timer expires
   */
  public watchIdle(): Observable<void> {
    return this.idleTimerSubject.asObservable();
  }

  /**
   * Returns an Observable that emits when the timeout timer expires
   */
  public watchTimeout(): Observable<void> {
    return this.timeoutTimerSubject.asObservable();
  }

  public startIdleTimer(): void {
    this.stopIdleTimer();

    this.idleSubscription = timer(this.idleMilliseconds)
      .pipe(take(1))
      .subscribe(() => {
        this.idleTimerSubject.next();
      });
  }

  public startTimeoutTimer(): void {
    if (!this.isTimeoutTimerStarted) {
      // Since we're checking timeout timer state with isTimeoutTimerStarted, there shouldn't ever be a timeout timer already started when trying to start one here
      // but cleanup any possible existing subscription, just in case. Be defensive.
      this.stopTimeoutTimer();

      this.timeoutSubscription = timer(this.timeoutMilliseconds)
        .pipe(take(1))
        .subscribe(() => {
          this.timeoutTimerSubject.next();
        });

      this.isTimeoutTimerStarted = true;
    }
  }

  public stopIdleTimer(): void {
    if (!this.idleSubscription.closed) {
      this.idleSubscription.unsubscribe();
      this.idleSubscription = new Subscription();
    }
  }

  public stopTimeoutTimer(): void {
    if (!this.timeoutSubscription.closed) {
      this.timeoutSubscription.unsubscribe();
      this.timeoutSubscription = new Subscription();
      this.isTimeoutTimerStarted = false;
    }
  }

  public resetIdleTimer(): void {
    this.startIdleTimer();
  }
}
